<!-- StatusStepper.vue - where a report is in its life as a vertical timeline (Figma: Status stepper, variant B).
     Reported, in review, planned, fixed. Three dot shapes so the state never depends on colour:
     check = done, ring with core = current, empty outline = still open. The city can add a short note. -->
<template>
  <ol class="verlauf" :aria-label="$t('report.detail.status')">
    <li
      v-for="(s, i) in steps"
      :key="s"
      :class="{ done: i + 1 < step, current: i + 1 === step, open: i + 1 > step }"
      :aria-current="i + 1 === step ? 'step' : undefined"
    >
      <span class="rail" aria-hidden="true">
        <span class="dot">
          <svg v-if="i + 1 < step" viewBox="0 0 16 16" width="16" height="16" focusable="false">
            <path d="M4 8.5l2.7 2.7L12 5.3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span v-else-if="i + 1 === step" class="core"></span>
        </span>
        <span v-if="i < steps.length - 1" class="line"></span>
      </span>

      <div class="body">
        <p class="head">
          <span class="label">{{ $t('report.detail.steps.' + s) }}</span>
          <span v-if="i + 1 < step" class="sr-only">, {{ $t('report.detail.stepState.done') }}</span>
          <span v-else-if="i + 1 === step" class="sr-only">, {{ $t('report.detail.stepState.current') }}</span>
          <span v-if="dateFor(i + 1)" class="date">{{ dateFor(i + 1) }}</span>
        </p>

        <template v-if="i + 1 === step">
          <p class="desc">{{ $t('report.detail.stepsText.' + s) }}</p>
          <div v-if="note" class="note">
            <p class="note-title">{{ noteTitle }}</p>
            <p class="note-text">{{ note }}</p>
          </div>
        </template>
        <p v-else-if="i + 1 > step" class="desc muted">{{ $t('report.detail.stepOpen') }}</p>
      </div>
    </li>
  </ol>
</template>

<script>
export default {
  name: 'StatusStepper',
  props: {
    // 1 = reported, 2 = in review, 3 = planned, 4 = fixed
    step: { type: Number, default: 1 },
    // When the report was created, when its status last changed, optional note from the city and its date
    reportedAt: { type: String, default: '' },
    statusAt: { type: String, default: '' },
    note: { type: String, default: '' },
    noteAt: { type: String, default: '' },
  },
  data() {
    return { steps: ['reported', 'review', 'planned', 'fixed'] };
  },
  computed: {
    noteTitle() {
      const d = this.formatDate(this.noteAt);
      const t = this.$t('report.detail.noteTitle');
      return d ? `${t} · ${d}` : t;
    },
  },
  methods: {
    formatDate(v) {
      if (!v) return '';
      const d = new Date(String(v).replace(' ', 'T'));
      if (isNaN(d)) return '';
      return d.toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short' });
    },
    // Reported shows when it was reported, the current step shows since when. Other steps show no date: the data has none.
    dateFor(n) {
      if (n === 1) return this.formatDate(this.reportedAt);
      if (n === this.step) {
        const d = this.formatDate(this.statusAt);
        if (!d) return '';
        return this.$t(n === 4 ? 'report.detail.on' : 'report.detail.since', { date: d });
      }
      return '';
    },
  },
};
</script>

<style scoped>
.verlauf { display: flex; flex-direction: column; margin: 0; padding: 0; list-style: none; text-align: left; }
.verlauf li { display: flex; gap: 12px; align-items: stretch; }
.rail { display: flex; flex-direction: column; align-items: center; width: 20px; flex: none; }
.dot {
  display: flex; align-items: center; justify-content: center; box-sizing: border-box;
  width: 16px; height: 16px; margin-top: 2px; border-radius: 50%; flex: none;
}
.line { flex: 1; width: 2px; min-height: 8px; background: var(--color-border); }
.body { flex: 1; min-width: 0; padding-bottom: 16px; display: flex; flex-direction: column; gap: 4px; }
li:last-child .body { padding-bottom: 0; }
.head { margin: 0; display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; font-size: 16px; }
.label { color: var(--color-secondary); }
.date { font-size: 14px; color: var(--color-secondary); }
.desc { margin: 0; font-size: 14px; color: var(--color-text); overflow-wrap: anywhere; }
.desc.muted { color: var(--color-secondary); }
:global(body.dark-mode) .desc:not(.muted) { color: var(--color-text-light); }

/* done: filled dot with a check */
.done .dot { background: var(--color-status-done); color: var(--color-on-status-done); }
.done .label { color: var(--color-text); }
.done .line { background: var(--color-status-done); }
/* current: ring with a core, a little bigger */
.current .dot { width: 20px; height: 20px; margin-top: 0; background: var(--color-surface); border: 3px solid var(--color-status-done); }
.current .core { width: 8px; height: 8px; border-radius: 50%; background: var(--color-status-done); }
.current .label { color: var(--color-text); font-weight: 600; }
.current .line { background: var(--color-border); }
/* open: empty outline */
.open .dot { border: 2px solid var(--color-border-input); background: transparent; }

.note { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; padding: 10px 12px; border-left: 4px solid var(--color-status-done); border-radius: var(--radius-md); background: var(--color-subtle); }
.note p { margin: 0; overflow-wrap: anywhere; white-space: pre-line; color: var(--color-text); }
:global(body.dark-mode) .note p { color: var(--color-text-light); }
.note-title { font-size: 14px; font-weight: 600; }
.note-text { font-size: 14px; }

@media (forced-colors: active) {
  .dot { border: 2px solid CanvasText; }
  .done .dot { background: CanvasText; color: Canvas; }
  .line { background: CanvasText; }
}
</style>
